import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import confetti from "canvas-confetti";
import { api, PLAN_LABELS } from "../lib/config";
import { Loader2, ArrowLeft, Search, Users, Sparkles, X, LogIn, Tv, Gift, Trophy, CheckCircle2, Clock, Heart } from "lucide-react";
import { Button } from "../components/ui/button";

const POLL_MS = 10000;
const PUBLIC_URL = "https://rescate.iglesiavenyve.org";

export default function PublicParticipants() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tvMode = params.get("mode") === "tv";

  const [progress, setProgress] = useState(null);
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortMode, setSortMode] = useState("newest");
  const [celebrate, setCelebrate] = useState(null);
  const previousWinnersRef = useRef(new Set());

  // Fetch
  const load = async () => {
    try {
      const [p, pr] = await Promise.all([
        api.get("/public/progress", { params: { limit: 1000, all_photos: true } }),
        api.get("/public/promotions"),
      ]);
      setProgress(p.data);
      const newPromos = pr.data.promotions || [];
      // Detect new winners
      const currentIds = new Set();
      newPromos.forEach((promo) => promo.winners.forEach((w) => currentIds.add(`${promo.id}:${w.position}`)));
      if (previousWinnersRef.current.size > 0) {
        const newOnes = [...currentIds].filter((x) => !previousWinnersRef.current.has(x));
        if (newOnes.length > 0) {
          // Find the newest winner
          for (const promo of newPromos) {
            for (const w of promo.winners) {
              if (!previousWinnersRef.current.has(`${promo.id}:${w.position}`)) {
                triggerCelebration(promo, w);
                break;
              }
            }
          }
        }
      }
      previousWinnersRef.current = currentIds;
      setPromos(newPromos);
    } catch (e) {/* */}
    finally { setLoading(false); }
  };

  useEffect(() => {
    document.title = tvMode ? "TV · Operación Rescate" : "Inscritos · Operación Rescate";
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [tvMode]); // eslint-disable-line

  const triggerCelebration = (promo, winner) => {
    setCelebrate({ promo, winner });
    // Play victory fanfare
    playVictorySound();
    // Multi-burst confetti
    const colors = ["#FBBF24", "#F97316", "#FCD34D", "#FFFFFF"];
    const burst = () => {
      confetti({ particleCount: 80, spread: 90, origin: { y: 0.6 }, colors });
      confetti({ particleCount: 60, spread: 130, origin: { x: 0.2, y: 0.7 }, colors });
      confetti({ particleCount: 60, spread: 130, origin: { x: 0.8, y: 0.7 }, colors });
    };
    burst();
    setTimeout(burst, 600);
    setTimeout(burst, 1200);
    setTimeout(() => setCelebrate(null), 6000);
  };

  const playVictorySound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      // C major arpeggio + held high note = fanfare
      // Frequencies: C5, E5, G5, C6 then sustained
      const notes = [
        { freq: 523.25, start: 0, dur: 0.18 },     // C5
        { freq: 659.25, start: 0.15, dur: 0.18 },  // E5
        { freq: 783.99, start: 0.30, dur: 0.18 },  // G5
        { freq: 1046.5, start: 0.45, dur: 0.7 },   // C6 sustained
        { freq: 1318.5, start: 1.0, dur: 0.7 },    // E6 sustained
        { freq: 1568.0, start: 1.55, dur: 1.5 },   // G6 final long
      ];
      const now = ctx.currentTime;
      notes.forEach((n) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.value = n.freq;
        osc.connect(gain).connect(ctx.destination);
        gain.gain.setValueAtTime(0.0001, now + n.start);
        gain.gain.exponentialRampToValueAtTime(0.35, now + n.start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + n.start + n.dur);
        osc.start(now + n.start);
        osc.stop(now + n.start + n.dur + 0.05);
      });
      // Cymbal / sparkle on top
      const noise = ctx.createBufferSource();
      const buf = ctx.createBuffer(1, ctx.sampleRate * 0.35, ctx.sampleRate);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < ch.length; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / ch.length);
      noise.buffer = buf;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 5000;
      const ng = ctx.createGain();
      ng.gain.value = 0.18;
      noise.connect(hp).connect(ng).connect(ctx.destination);
      noise.start(now);
    } catch (e) { /* silent fail */ }
  };

  const filtered = useMemo(() => {
    if (!progress?.registrants) return [];
    const norm = (s) => (s || "").toString().normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
    const q = norm(query.trim());
    let list = q
      ? progress.registrants.filter((r) =>
          norm(r.display_name).includes(q) || norm(r.plan_type).includes(q) || norm(r.age_category).includes(q)
        )
      : [...progress.registrants];
    list.sort((a, b) => {
      switch (sortMode) {
        case "progress_desc": return b.percentage_paid - a.percentage_paid;
        case "progress_asc": return a.percentage_paid - b.percentage_paid;
        case "name": return (a.display_name || "").localeCompare(b.display_name || "");
        case "newest":
        default: return (b.joined_at || "").localeCompare(a.joined_at || "");
      }
    });
    return list;
  }, [progress, query, sortMode]);

  const stats = progress?.stats_by_installment || {};
  const total = progress?.total_registered || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-yellow-400 animate-spin" />
      </div>
    );
  }

  // ============== TV MODE ==============
  if (tvMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white relative overflow-hidden flex flex-col">
        <div className="absolute -top-40 left-1/4 w-[40rem] h-[40rem] bg-yellow-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[40rem] h-[40rem] bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
        <CelebrationOverlay celebrate={celebrate} tvMode />

        <div className="relative flex-1 flex flex-col p-8 lg:p-12">
          <div className="text-center mb-8">
            <div className="inline-block px-6 py-2 border border-yellow-400/60 text-yellow-400 text-xs sm:text-sm uppercase tracking-[0.5em] font-bold mb-4">
              <Sparkles className="w-4 h-4 inline mr-2" /> Operación Rescate · En Vivo
            </div>
            <h1 className="font-heading uppercase text-6xl sm:text-8xl lg:text-9xl tracking-tighter leading-none">
              <span className="text-gradient-gold">Familia</span> Rescate
            </h1>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <BigStat n={total} label="Inscritos" color="blue" icon={Users} />
            <BigStat n={stats.first_week || 0} label="Pagaron 1ra cuota" color="green" icon={CheckCircle2} />
            <BigStat n={stats.second_week + (stats.third_plus || 0) + (stats.fully_paid || 0)} label="Con 2+ cuotas" color="gold" icon={Trophy} />
            <BigStat n={`${progress?.global_percentage || 0}%`} label="Avance global" color="orange" icon={Sparkles} />
          </div>

          {promos.length > 0 && (
            <div className="mb-8">
              {promos.slice(0, 1).map((p) => <PromoBanner key={p.id} promo={p} tvMode />)}
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 flex-1">
            {filtered.slice(0, 9).map((r, i) => (
              <BigParticipantCard key={i} r={r} />
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-6">
            <div>
              <div className="text-xs uppercase tracking-[0.4em] text-yellow-400 font-bold mb-1">Escanea e inscríbete</div>
              <div className="text-3xl font-heading">rescate.iglesiavenyve.org</div>
            </div>
            <div className="bg-white p-3 rounded-2xl">
              <QRCodeSVG value={PUBLIC_URL} size={140} level="H" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============== NORMAL MODE ==============
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white">
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="absolute -top-40 left-1/4 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
      <CelebrationOverlay celebrate={celebrate} />

      <div className="relative max-w-6xl mx-auto px-6 py-10 lg:px-12 lg:py-16">
        <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
          <Button variant="ghost" onClick={() => nav("/")} className="text-white hover:text-yellow-400 hover:bg-white/5" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4 mr-2" /> Inicio
          </Button>
          <div className="flex items-center gap-2">
            <Button onClick={() => nav("/ganadores")} variant="outline" className="bg-yellow-400/10 border-yellow-400/50 text-yellow-200 hover:bg-yellow-400/20 hover:text-yellow-100 font-bold" data-testid="see-winners-btn">
              <Trophy className="w-4 h-4 mr-2" /> Ver Ganadores
            </Button>
            <Button onClick={() => nav("/inscritos?mode=tv")} variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="tv-mode-btn">
              <Tv className="w-4 h-4 mr-2" /> Modo TV
            </Button>
            <Button onClick={() => nav("/login")} data-testid="public-my-account-button" className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wider">
              <LogIn className="w-4 h-4 mr-2" /> Ver mi pago
            </Button>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center mb-10">
          <span className="inline-block px-4 py-2 border border-yellow-400/40 bg-yellow-400/10 text-yellow-400 text-xs uppercase tracking-[0.4em] font-bold mb-6">
            <Sparkles className="w-3 h-3 inline mr-2" /> Inscritos del Campamento
          </span>
          <h1 className="font-heading uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
            Familia <span className="text-gradient-gold">Operación Rescate</span>
          </h1>
          <p className="text-white/70 mt-4 text-base sm:text-lg">
            Todos los jóvenes inscritos rumbo al 28 de Agosto.
          </p>
        </motion.div>

        {/* Status stats (NO money) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatusCard n={total} label="Inscritos" color="blue" icon={Users} />
          <StatusCard n={stats.first_week || 0} label="1ra cuota" color="yellow" icon={Clock} />
          <StatusCard n={(stats.second_week || 0) + (stats.third_plus || 0) + (stats.fully_paid || 0)} label="2+ cuotas" color="green" icon={CheckCircle2} />
          <StatusCard n={stats.none || 0} label="Sin pagar" color="red" icon={Heart} />
        </div>

        {/* Global progress bar (NO money values) */}
        <div className="mb-10 bg-white/5 border border-white/10 rounded-2xl p-5">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-xs uppercase tracking-[0.3em] text-yellow-400 font-bold">Avance global</div>
            <div className="font-heading text-3xl text-gradient-gold">{progress?.global_percentage || 0}%</div>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${progress?.global_percentage || 0}%` }} transition={{ duration: 1.2 }} className="h-full bg-gradient-to-r from-yellow-400 to-orange-500" />
          </div>
        </div>

        {/* Active promotions */}
        {promos.map((p) => <PromoBanner key={p.id} promo={p} />)}

        {/* QR section */}
        <div className="bg-gradient-to-br from-yellow-400/10 to-orange-500/10 border border-yellow-400/30 rounded-3xl p-6 sm:p-8 mb-10 flex flex-col sm:flex-row items-center gap-6">
          <div className="bg-white p-3 rounded-2xl flex-shrink-0">
            <QRCodeSVG value={PUBLIC_URL} size={160} level="H" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="text-xs uppercase tracking-[0.4em] text-yellow-400 font-bold mb-2">Escanea con tu celular</div>
            <h3 className="font-heading uppercase text-3xl sm:text-4xl mb-2">Inscríbete ahora</h3>
            <p className="text-white/70 text-sm sm:text-base">
              {promos[0]?.public_text || "Comparte con tus amigos y rescata vidas juntos."}
            </p>
          </div>
        </div>

        {/* Search & sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
            <input
              type="text" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre o plan..." data-testid="participants-search"
              className="w-full h-12 pl-10 pr-10 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white" data-testid="participants-clear"><X className="w-4 h-4" /></button>
            )}
          </div>
          <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} data-testid="participants-sort" className="h-12 px-4 rounded-xl bg-white/5 border border-white/15 text-white font-semibold text-sm focus:outline-none focus:border-yellow-400/60">
            <option value="newest" className="bg-zinc-900">🆕 Más recientes</option>
            <option value="progress_desc" className="bg-zinc-900">🏆 Mayor avance</option>
            <option value="progress_asc" className="bg-zinc-900">📈 Menor avance</option>
            <option value="name" className="bg-zinc-900">🔤 Nombre A-Z</option>
          </select>
        </div>

        <div className="text-xs text-white/50 uppercase tracking-widest font-bold mb-4" data-testid="participants-count">
          {query ? `Mostrando ${filtered.length} de ${progress?.registrants?.length || 0}` : `${filtered.length} ${filtered.length === 1 ? "joven inscrito" : "jóvenes inscritos"}`}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 text-white/60"><Users className="w-12 h-12 mx-auto mb-3 text-white/30" /><p>No se encontraron inscritos.</p></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="participants-grid">
            {filtered.map((r, i) => <ParticipantCard key={i} r={r} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function colorClass(c, variant = "bg") {
  const map = {
    bg: { blue: "bg-blue-500/15 border-blue-400/40", yellow: "bg-yellow-500/15 border-yellow-400/40", green: "bg-green-500/15 border-green-400/40", red: "bg-red-500/15 border-red-400/40", gold: "bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-400/40", orange: "bg-orange-500/15 border-orange-400/40", purple: "bg-purple-500/15 border-purple-400/40" },
    text: { blue: "text-blue-300", yellow: "text-yellow-300", green: "text-green-300", red: "text-red-300", gold: "text-gradient-gold", orange: "text-orange-300", purple: "text-purple-300" },
  };
  return map[variant][c] || "";
}

function StatusCard({ n, label, color, icon: Icon }) {
  return (
    <div className={`rounded-2xl p-4 border ${colorClass(color)}`}>
      <Icon className={`w-4 h-4 mb-2 ${colorClass(color, "text")}`} />
      <div className={`font-heading text-3xl ${colorClass(color, "text")}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">{label}</div>
    </div>
  );
}

function BigStat({ n, label, color, icon: Icon }) {
  return (
    <div className={`rounded-3xl p-8 border-2 ${colorClass(color)}`}>
      <Icon className={`w-10 h-10 mb-3 ${colorClass(color, "text")}`} />
      <div className={`font-heading text-7xl lg:text-8xl ${colorClass(color, "text")}`}>{n}</div>
      <div className="text-sm lg:text-base uppercase tracking-[0.3em] text-white/80 font-bold mt-2">{label}</div>
    </div>
  );
}

function ParticipantCard({ r, index }) {
  const pct = r.percentage_paid || 0;
  const initial = (r.display_name?.[0] || "?").toUpperCase();
  const statusColor = pct >= 100 ? "green" : pct >= 50 ? "gold" : pct > 0 ? "yellow" : "blue";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: Math.min(index * 0.02, 0.4) }}
      className={`bg-white/5 border rounded-2xl p-4 hover:border-yellow-400/40 transition-colors ${pct >= 100 ? "border-green-400/40" : "border-white/10"}`}
      data-testid={`participant-card-${index}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-14 h-14 rounded-full border-2 ${pct >= 100 ? "border-green-400" : "border-yellow-400/60"} overflow-hidden flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-yellow-500/20 to-orange-500/20`}>
          {r.photo ? <img src={r.photo} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-2xl text-yellow-400">{initial}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-white truncate">{r.display_name}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/50">
            {r.age_category === "adult" ? "Adulto" : r.age_category === "child" ? "Niño" : "3-6"} · {PLAN_LABELS[r.plan_type]?.toLowerCase()}
          </div>
        </div>
      </div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className={`font-heading text-2xl ${colorClass(statusColor, "text")}`}>{pct}%</span>
        <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{r.installments_paid}/{r.num_installments} cuotas</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(pct, 100)}%` }} transition={{ duration: 1, delay: Math.min(index * 0.02, 0.4) }}
          className={`h-full ${pct >= 100 ? "bg-green-400" : "bg-gradient-to-r from-yellow-400 to-orange-500"}`} />
      </div>
    </motion.div>
  );
}

function BigParticipantCard({ r }) {
  const pct = r.percentage_paid || 0;
  return (
    <div className={`bg-white/5 border-2 rounded-3xl p-6 ${pct >= 100 ? "border-green-400/60" : "border-yellow-400/20"}`}>
      <div className="flex items-center gap-4 mb-4">
        <div className={`w-20 h-20 rounded-full border-4 ${pct >= 100 ? "border-green-400" : "border-yellow-400"} overflow-hidden bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center`}>
          {r.photo ? <img src={r.photo} alt="" className="w-full h-full object-cover" /> : <span className="font-heading text-3xl text-yellow-400">{(r.display_name?.[0] || "?").toUpperCase()}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-2xl text-white truncate">{r.display_name}</div>
          <div className="text-xs uppercase tracking-widest text-yellow-400/80">{PLAN_LABELS[r.plan_type]?.toLowerCase()}</div>
        </div>
        <div className={`font-heading text-5xl ${pct >= 100 ? "text-green-400" : "text-gradient-gold"}`}>{pct}%</div>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full ${pct >= 100 ? "bg-green-400" : "bg-gradient-to-r from-yellow-400 to-orange-500"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

function PromoBanner({ promo, tvMode }) {
  const pct = (promo.winners_count / promo.max_winners) * 100;
  const isFull = promo.slots_remaining === 0;
  return (
    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`relative rounded-3xl p-5 mb-6 border-2 ${isFull ? "border-green-400/60 bg-gradient-to-br from-green-500/15 via-emerald-500/10 to-yellow-500/15" : "border-purple-400/60 bg-gradient-to-br from-purple-500/15 via-pink-500/10 to-yellow-500/15"} overflow-hidden ${tvMode ? "text-2xl" : ""}`}>
      <div className="flex items-start gap-3">
        <Gift className={`${tvMode ? "w-12 h-12" : "w-6 h-6"} ${isFull ? "text-green-300" : "text-purple-300"} flex-shrink-0`} />
        <div className="flex-1">
          <div className={`text-xs uppercase tracking-[0.3em] font-bold mb-1 ${tvMode ? "text-base" : ""} ${isFull ? "text-green-300" : "text-purple-300"}`}>
            {isFull ? "🏆 Cupos completos" : "Promoción Especial"}
          </div>
          <h3 className={`font-heading uppercase ${tvMode ? "text-4xl lg:text-5xl" : "text-2xl"} mb-2`}>{promo.name}</h3>
          <p className={`text-white/80 mb-3 ${tvMode ? "text-xl" : "text-sm"}`}>{promo.public_text}</p>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`font-bold ${tvMode ? "text-2xl" : "text-sm"} ${isFull ? "text-green-200" : "text-purple-200"}`}>{promo.winners_count}/{promo.max_winners}</span>
            <span className={`text-white/60 ${tvMode ? "text-lg" : "text-xs"}`}>ganadores</span>
            {!isFull ? (
              <span className={`ml-auto text-yellow-300 font-bold ${tvMode ? "text-xl" : "text-xs"}`}>¡Quedan {promo.slots_remaining} lugar{promo.slots_remaining > 1 ? "es" : ""}!</span>
            ) : (
              <span className={`ml-auto text-green-200 font-bold ${tvMode ? "text-xl" : "text-xs"}`}>✓ ¡Sigue inscribiéndote, tu apoyo cuenta igual!</span>
            )}
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
            <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} className={`h-full ${isFull ? "bg-gradient-to-r from-green-400 to-emerald-500" : "bg-gradient-to-r from-purple-400 to-pink-500"}`} />
          </div>
          {promo.winners.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {promo.winners.map((w) => (
                <div key={w.position} className={`inline-flex items-center gap-1.5 ${tvMode ? "px-3 py-1.5 text-base" : "px-2 py-1 text-xs"} ${isFull ? "bg-green-500/30 border-green-300/50" : "bg-purple-500/30 border-purple-300/50"} border rounded-full font-bold`}>
                  <Trophy className={tvMode ? "w-4 h-4" : "w-3 h-3"} />#{w.position} {w.display_name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function CelebrationOverlay({ celebrate, tvMode }) {
  return (
    <AnimatePresence>
      {celebrate && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center pointer-events-none px-6"
        >
          <motion.div
            initial={{ scale: 0.5, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200 }}
            className={`bg-gradient-to-br from-purple-600 via-pink-500 to-yellow-500 rounded-3xl p-8 sm:p-12 shadow-2xl border-4 border-white text-center ${tvMode ? "scale-150" : ""}`}
          >
            <div className="text-6xl sm:text-8xl mb-4">🎉</div>
            <div className="text-xs uppercase tracking-[0.5em] text-white/90 font-bold mb-2">¡Nuevo Ganador!</div>
            <div className="font-heading text-5xl sm:text-7xl text-white mb-2">#{celebrate.winner.position}</div>
            <div className="font-heading text-3xl sm:text-5xl text-white mb-3">{celebrate.winner.display_name}</div>
            <div className="text-white/90 text-sm sm:text-base">acaba de calificar en <strong>{celebrate.promo.name}</strong></div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
